import { useEffect, useRef } from 'react';
import { failureReason } from '@/lib/conversations/precondition-failure';
import { PREVIEW_GIT_STATUS } from '@/lib/desktop-preview-data';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { isLocalMachine } from '@/lib/local-runtime';
import { readNativeGitStatus } from '@/lib/native-desktop';
import { subscribeNativeGitChanges } from '@/lib/native-desktop-transport';

/** Poll interval (ms) for refreshing the native git status of the open workspace. */
const GIT_STATUS_POLL_MS = 30_000;
export const GIT_STATUS_RETRY_MS = 5 * 60_000;

/**
 * Keeps the store's git status in sync with the on-disk native checkout: seeds
 * preview data, clears status for cloud/non-native workspaces, and otherwise
 * polls `readNativeGitStatus` on an interval and on window focus — reconciling
 * the managed-worktree association, execution mode, and source root. Extracted
 * verbatim from the inline effect in `DesktopShell`.
 */
export function useGitStatusSync({
  store,
  preview,
  workspace,
  chatId,
  executionMode = 'local',
}: {
  store: DesktopShellStore;
  preview: boolean;
  workspace: string | null;
  chatId: string;
  executionMode?: 'local' | 'worktree' | 'cloud';
}): void {
  const retryDeadlines = useRef(new Map<string, number>());
  // biome-ignore lint/correctness/useExhaustiveDependencies(chatId): another conversation reads the status again at once (its worktree is its own).
  useEffect(() => {
    const { setGitStatus, setGitStatusState, setExecutionMode } = store.getState();
    if (preview) {
      setGitStatus(PREVIEW_GIT_STATUS);
      setGitStatusState('ready');
      return;
    }
    if (workspace === null || !isLocalMachine() || executionMode === 'cloud') {
      if (executionMode !== 'cloud') setGitStatus(null);
      setGitStatusState('idle');
      return;
    }

    let cancelled = false;
    let inFlight = false;
    let dirty = false;
    // A failed workspace keeps its state and deadline across chat-scoped reruns.
    if (retryDeadlines.current.has(workspace)) {
      setGitStatus(null);
      setGitStatusState('unavailable');
    } else if (store.getState().gitStatus === null) {
      setGitStatusState('loading');
    }
    const refresh = async (): Promise<void> => {
      if (cancelled || document.visibilityState === 'hidden') return;
      if (Date.now() < (retryDeadlines.current.get(workspace) ?? 0)) return;
      if (inFlight) {
        dirty = true;
        return;
      }
      inFlight = true;
      dirty = false;
      if (store.getState().gitStatus === null && !retryDeadlines.current.has(workspace)) {
        setGitStatusState('loading');
      }
      try {
        const nextStatus = await readNativeGitStatus(workspace);
        if (!cancelled) {
          retryDeadlines.current.delete(workspace);
          setGitStatus(nextStatus);
          setGitStatusState('ready');
          // Which worktree is the conversation's is the server's record; a managed one's
          // folder only says the conversation runs in a worktree.
          const currentWorktree = nextStatus.worktrees.find((worktree) => worktree.isCurrent);
          if (currentWorktree?.managed === true)
            setExecutionMode((current) => (current === 'cloud' ? current : 'worktree'));
        }
      } catch (error) {
        if (!cancelled) {
          retryDeadlines.current.set(workspace, Date.now() + GIT_STATUS_RETRY_MS);
          setGitStatus(null);
          // Why there is no status: a plain folder, a folder gone, or git failing.
          const reason = failureReason(error);
          setGitStatusState(
            reason === 'not_a_repository'
              ? 'not-repository'
              : reason === 'folder_missing'
                ? 'folder-missing'
                : 'unavailable',
          );
        }
      } finally {
        inFlight = false;
        if (dirty && !cancelled) void refresh();
      }
    };
    const unsubscribe = subscribeNativeGitChanges(workspace, () => void refresh());
    void refresh();
    const interval = window.setInterval(() => void refresh(), GIT_STATUS_POLL_MS);
    const refreshOnFocus = (): void => void refresh();
    window.addEventListener('focus', refreshOnFocus);
    document.addEventListener('visibilitychange', refreshOnFocus);
    return () => {
      cancelled = true;
      unsubscribe();
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshOnFocus);
      document.removeEventListener('visibilitychange', refreshOnFocus);
    };
  }, [store, preview, workspace, chatId, executionMode]);
}
